package com.trevorwiebe.timesheet.mileage.data

import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.mileage.data.dto.LocationDto
import com.trevorwiebe.timesheet.mileage.data.dto.toLocation
import com.trevorwiebe.timesheet.mileage.data.dto.toLocationDto
import com.trevorwiebe.timesheet.mileage.domain.LocationRepository
import com.trevorwiebe.timesheet.mileage.domain.findMatchingLocation
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class LocationRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository
) : LocationRepository {

    override suspend fun getLocations(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return kotlinx.coroutines.flow.flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val flow = firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("locations")
            .snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<LocationDto>().toLocation(id = doc.id) }
                    .sortedBy { it.name.lowercase() }
            }
            .map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun addLocation(location: Location): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val collection = firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("locations")

            // Dedup chokepoint: every location-creating path (manual entry, receipt
            // scan, admin add) routes through here. Reuse an existing non-deleted
            // location of the same type/name/city/state instead of creating a duplicate.
            val existing = collection.get().documents
                .map { doc -> doc.data<LocationDto>().toLocation(id = doc.id) }
                .findMatchingLocation(location.type, location.name, location.city, location.state)
            if (existing != null) {
                return@errorWrapper TSResult(data = existing.id)
            }

            val result = collection.add(location.toLocationDto()) { encodeDefaults = true }
            TSResult(data = result.id)
        }
    }

    override suspend fun updateLocation(location: Location): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("locations")
                .document(location.id)
                .set(location.toLocationDto(), merge = true) { encodeDefaults = true }
            TSResult(data = "Location updated successfully")
        }
    }

    override suspend fun mergeLocations(keeperId: String, absorbedIds: List<String>): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val firestore = firebaseDatabase.firestore
            val orgDocument = firestore
                .collection("organizations")
                .document(orgId)
            val mileageEntries = orgDocument.collection("mileageEntries")
            val locations = orgDocument.collection("locations")

            // Collect every mileage entry that references an absorbed location.
            // locationId is stored as a flat field for FUEL/LOADING/UNLOADING/OTHER entries,
            // so a single equality query finds them regardless of trip type.
            val entryIdsToRepoint = absorbedIds.flatMap { absorbedId ->
                mileageEntries
                    .where { "locationId" equalTo absorbedId }
                    .get()
                    .documents
                    .map { it.id }
            }

            // A WriteBatch applies all writes atomically — either every entry is
            // re-pointed and every absorbed location deleted, or nothing changes —
            // but Firestore caps a batch at 500 operations. Bail out with a clear
            // message rather than silently splitting into non-atomic chunks.
            val totalOps = entryIdsToRepoint.size + absorbedIds.size
            if (totalOps > MAX_BATCH_OPERATIONS) {
                return@errorWrapper TSResult(
                    error = "This merge would change $totalOps records, over the " +
                        "$MAX_BATCH_OPERATIONS-record limit for an atomic merge. " +
                        "Merge fewer locations at a time."
                )
            }

            val batch = firestore.batch()
            // Re-point every referencing entry to the keeper.
            entryIdsToRepoint.forEach { entryId ->
                batch.update(mileageEntries.document(entryId), "locationId" to keeperId)
            }
            // Delete the absorbed locations in the same batch.
            absorbedIds.forEach { absorbedId ->
                batch.delete(locations.document(absorbedId))
            }
            batch.commit()

            TSResult(data = "Locations merged successfully")
        }
    }

    private companion object {
        // Firestore's maximum number of writes in a single atomic batch.
        const val MAX_BATCH_OPERATIONS = 500
    }
}
